import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import axiosHttp from "services/axiosHttp";

// Async thunks for file operations
export const createDmsFile = createAsyncThunk("createDmsFile", async (data) => {
	const response = await axiosHttp.post("/dms/files", data);
	return response.data;
});

export const fetchFileList = createAsyncThunk("fetchFileList", async (params) => {
	const response = await axiosHttp.get("/dms/files", { params });
	return response.data;
});

export const fetchFileDetail = createAsyncThunk("fetchFileDetail", async (uuid) => {
	const response = await axiosHttp.get(`/dms/file/${uuid}`);
	return response.data;
});

export const updateDmsFile = createAsyncThunk("updateDmsFile", async ({ uuid, data }) => {
	const response = await axiosHttp.put(`/dms/files/${uuid}`, data);
	return response.data;
});

export const moveDmsFile = createAsyncThunk("moveDmsFile", async ({ uuid, data }) => {
	const response = await axiosHttp.post(`/dms/move-file/${uuid}`, data);
	return response.data;
});

export const deleteDmsFile = createAsyncThunk("deleteDmsFile", async ({ uuid, force = true }) => {
	const response = await axiosHttp.post(`/dms/delete-files/${uuid}`, {
		 delete_all_versions: true 
	});
	return response.data;
});

export const fetchFileVersions = createAsyncThunk("fetchFileVersions", async (uuid) => {
	const response = await axiosHttp.get(`/dms/file/${uuid}/versions`);
	return response.data;
});

export const fetchFileByVersion = createAsyncThunk("fetchFileByVersion", async ({ uuid, version }) => {
	const response = await axiosHttp.get(`/dms/file/${uuid}/version/${version}`);
	return response.data;
});

export const getShareableUrl = createAsyncThunk("getShareableUrl", async ({ uuid, data }) => {
	const response = await axiosHttp.post(`/dms/files/${uuid}/share`, data);
	return response.data;
});



export const downloadFile = createAsyncThunk("downloadFile", async ({ uuid, data }) => {
	const response = await axiosHttp.post(`/dms/files/${uuid}/download`, data);
	return response.data;
});

// Upload file to S3 using presigned URL
export const uploadFileToS3 = createAsyncThunk("uploadFileToS3", async ({ presignedUrl, file }) => {
	try {
		const response = await fetch(presignedUrl, {
			method: 'PUT',
			body: file,
			headers: {
				'Content-Type': file.type,
				'Access-Control-Allow-Origin':'*'
			},
		});
		
		if (!response.ok) {
			throw new Error('Upload failed');
		}
		
		return { success: true };
	} catch (error) {
		throw new Error(error.message);
	}
});

const fileSlice = createSlice({
	name: "dmsFileSlice",
	initialState: {
		list: {
			data: null,
			isLoading: false,
			isError: false,
			error: null,
		},
		detail: {
			data: null,
			isLoading: false,
			isError: false,
			error: null,
			showDrawer: false,
			uuid: null,
		},
		create: {
			isLoading: false,
			isError: false,
			error: null,
			data: null,
		},
		update: {
			isLoading: false,
			isError: false,
			error: null,
			data: null,
		},
		move: {
			isLoading: false,
			isError: false,
			error: null,
			data: null,
		},
		delete: {
			isLoading: false,
			isError: false,
			error: null,
			data: null,
		},
		versions: {
			data: null,
			isLoading: false,
			isError: false,
			error: null,
		},
		versionDetail: {
			data: null,
			isLoading: false,
			isError: false,
			error: null,
		},
		shareableUrl: {
			data: null,
			isLoading: false,
			isError: false,
			error: null,
		},
		upload: {
			isLoading: false,
			isError: false,
			error: null,
			progress: 0,
		},
		uploadModal: {
			isModalOpen: false,
		},
		shareModal: {
			isModalOpen: false,
			fileUuid: null,
		},
		renameModal: {
			isModalOpen: false,
			fileUuid: null,
		},
		moveModal: {
			isModalOpen: false,
			fileUuid: null,
		},
		deleteModal: {
			isModalOpen: false,
			fileUuid: null,
		},
		download: {
			isLoading: false,
			isError: false,
			error: null,
			data: null,
		},
	},
	reducers: {
		showFileDetailDrawer: (state, action) => {
			state.detail.showDrawer = true;
			state.detail.uuid = action.payload;
		},
		hideFileDetailDrawer: (state) => {
			state.detail.showDrawer = false;
			state.detail.uuid = null;
		},
		showUploadModal: (state, action) => {
			state.uploadModal.isModalOpen = true;
		},
		hideUploadModal: (state) => {
			state.uploadModal.isModalOpen = false;
		},
		showShareModal: (state, action) => {
			state.shareModal.isModalOpen = true;
			state.shareModal.fileUuid = action.payload;
		},
		hideShareModal: (state) => {
			state.shareModal.isModalOpen = false;
			state.shareModal.fileUuid = null;
		},
		showRenameModal: (state, action) => {
			state.renameModal.isModalOpen = true;
			state.renameModal.fileUuid = action.payload;
			// Clear update state when opening modal
			state.update.data = null;
			state.update.isError = false;
			state.update.error = null;
		},
		hideRenameModal: (state) => {
			state.renameModal.isModalOpen = false;
			state.renameModal.fileUuid = null;
		},
		showMoveModal: (state, action) => {
			state.moveModal.isModalOpen = true;
			state.moveModal.fileUuid = action.payload;
			// Clear move state when opening modal
			state.move.data = null;
			state.move.isError = false;
			state.move.error = null;
		},
		hideMoveModal: (state) => {
			state.moveModal.isModalOpen = false;
			state.moveModal.fileUuid = null;
		},
		showDeleteModal: (state, action) => {
			state.deleteModal.isModalOpen = true;
			state.deleteModal.fileUuid = action.payload;
			// Clear delete state when opening modal
			state.delete.data = null;
			state.delete.isError = false;
			state.delete.error = null;
		},
		hideDeleteModal: (state) => {
			state.deleteModal.isModalOpen = false;
			state.deleteModal.fileUuid = null;
		},
		setUploadProgress: (state, action) => {
			state.upload.progress = action.payload;
		},
		resetFileState: (state) => {
			state.create.data = null;
			state.create.isLoading = false;
			state.create.isError = false;
			state.create.error = null;
			state.update.data = null;
			state.update.isLoading = false;
			state.update.isError = false;
			state.update.error = null;
			state.move.data = null;
			state.move.isLoading = false;
			state.move.isError = false;
			state.move.error = null;
			state.delete.data = null;
			state.delete.isLoading = false;
			state.delete.isError = false;
			state.delete.error = null;
			state.upload.isLoading = false;
			state.upload.isError = false;
			state.upload.error = null;
			state.upload.progress = 0;
		},
		resetDownloadState: (state) => {
			state.download.isLoading = false;
			state.download.isError = false;
			state.download.error = null;
			state.download.data = null;
		},
	},
	extraReducers: (builder) => {
		// Create file
		builder.addCase(createDmsFile.pending, (state) => {
			state.create.isLoading = true;
			state.create.isError = false;
			state.create.error = null;
		});
		builder.addCase(createDmsFile.fulfilled, (state, action) => {
			state.create.isLoading = false;
			state.create.isError = false;
			state.create.error = null;
			state.create.data = action.payload;
		});
		builder.addCase(createDmsFile.rejected, (state, action) => {
			state.create.isLoading = false;
			state.create.isError = true;
			state.create.error = action.payload;
		});

		// Fetch file list
		builder.addCase(fetchFileList.pending, (state) => {
			state.list.isLoading = true;
			state.list.isError = false;
			state.list.error = null;
		});
		builder.addCase(fetchFileList.fulfilled, (state, action) => {
			state.list.isLoading = false;
			state.list.isError = false;
			state.list.error = null;
			state.list.data = action.payload;
		});
		builder.addCase(fetchFileList.rejected, (state, action) => {
			state.list.isLoading = false;
			state.list.isError = true;
			state.list.error = action.payload;
		});

		// Fetch file detail
		builder.addCase(fetchFileDetail.pending, (state) => {
			state.detail.isLoading = true;
			state.detail.isError = false;
			state.detail.error = null;
		});
		builder.addCase(fetchFileDetail.fulfilled, (state, action) => {
			state.detail.isLoading = false;
			state.detail.isError = false;
			state.detail.error = null;
			state.detail.data = action.payload;
		});
		builder.addCase(fetchFileDetail.rejected, (state, action) => {
			state.detail.isLoading = false;
			state.detail.isError = true;
			state.detail.error = action.payload;
		});

		// Update file
		builder.addCase(updateDmsFile.pending, (state) => {
			state.update.isLoading = true;
			state.update.isError = false;
			state.update.error = null;
		});
		builder.addCase(updateDmsFile.fulfilled, (state, action) => {
			state.update.isLoading = false;
			state.update.isError = false;
			state.update.error = null;
			state.update.data = action.payload;
		});
		builder.addCase(updateDmsFile.rejected, (state, action) => {
			state.update.isLoading = false;
			state.update.isError = true;
			state.update.error = action.payload;
		});

		// Move file
		builder.addCase(moveDmsFile.pending, (state) => {
			state.move.isLoading = true;
			state.move.isError = false;
			state.move.error = null;
		});
		builder.addCase(moveDmsFile.fulfilled, (state, action) => {
			state.move.isLoading = false;
			state.move.isError = false;
			state.move.error = null;
			state.move.data = action.payload;
		});
		builder.addCase(moveDmsFile.rejected, (state, action) => {
			state.move.isLoading = false;
			state.move.isError = true;
			state.move.error = action.payload;
		});

		// Delete file
		builder.addCase(deleteDmsFile.pending, (state) => {
			state.delete.isLoading = true;
			state.delete.isError = false;
			state.delete.error = null;
		});
		builder.addCase(deleteDmsFile.fulfilled, (state, action) => {
			state.delete.isLoading = false;
			state.delete.isError = false;
			state.delete.error = null;
			state.delete.data = action.payload;
		});
		builder.addCase(deleteDmsFile.rejected, (state, action) => {
			state.delete.isLoading = false;
			state.delete.isError = true;
			state.delete.error = action.payload;
		});

		// Fetch file versions
		builder.addCase(fetchFileVersions.pending, (state) => {
			state.versions.isLoading = true;
			state.versions.isError = false;
			state.versions.error = null;
		});
		builder.addCase(fetchFileVersions.fulfilled, (state, action) => {
			state.versions.isLoading = false;
			state.versions.isError = false;
			state.versions.error = null;
			state.versions.data = action.payload;
		});
		builder.addCase(fetchFileVersions.rejected, (state, action) => {
			state.versions.isLoading = false;
			state.versions.isError = true;
			state.versions.error = action.payload;
		});

		// Fetch file by version
		builder.addCase(fetchFileByVersion.pending, (state) => {
			state.versionDetail.isLoading = true;
			state.versionDetail.isError = false;
			state.versionDetail.error = null;
		});
		builder.addCase(fetchFileByVersion.fulfilled, (state, action) => {
			state.versionDetail.isLoading = false;
			state.versionDetail.isError = false;
			state.versionDetail.error = null;
			state.versionDetail.data = action.payload;
		});
		builder.addCase(fetchFileByVersion.rejected, (state, action) => {
			state.versionDetail.isLoading = false;
			state.versionDetail.isError = true;
			state.versionDetail.error = action.payload;
		});

		// Get shareable URL
		builder.addCase(getShareableUrl.pending, (state) => {
			state.shareableUrl.isLoading = true;
			state.shareableUrl.isError = false;
			state.shareableUrl.error = null;
		});
		builder.addCase(getShareableUrl.fulfilled, (state, action) => {
			state.shareableUrl.isLoading = false;
			state.shareableUrl.isError = false;
			state.shareableUrl.error = null;
			state.shareableUrl.data = action.payload;
		});
		builder.addCase(getShareableUrl.rejected, (state, action) => {
			state.shareableUrl.isLoading = false;
			state.shareableUrl.isError = true;
			state.shareableUrl.error = action.payload;
		});

		// Upload to S3
		builder.addCase(uploadFileToS3.pending, (state) => {
			state.upload.isLoading = true;
			state.upload.isError = false;
			state.upload.error = null;
		});
		builder.addCase(uploadFileToS3.fulfilled, (state, action) => {
			state.upload.isLoading = false;
			state.upload.isError = false;
			state.upload.error = null;
			state.upload.progress = 100;
		});
		builder.addCase(uploadFileToS3.rejected, (state, action) => {
			state.upload.isLoading = false;
			state.upload.isError = true;
			state.upload.error = action.payload;
			state.upload.progress = 0;
		});
		// Download file
		builder.addCase(downloadFile.pending, (state) => {
			state.download.isLoading = true;
			state.download.isError = false;
			state.download.error = null;
		});
		builder.addCase(downloadFile.fulfilled, (state, action) => {
			state.download.isLoading = false;
			state.download.isError = false;
			state.download.error = null;
			state.download.data = action.payload;
		});
		builder.addCase(downloadFile.rejected, (state, action) => {
			state.download.isLoading = false;
			state.download.isError = true;
			state.download.error = action.payload;
		});
	},
});

export const {
	showFileDetailDrawer,
	hideFileDetailDrawer,
	showUploadModal,
	hideUploadModal,
	showShareModal,
	hideShareModal,
	showRenameModal,
	hideRenameModal,
	showMoveModal,
	hideMoveModal,
	showDeleteModal,
	hideDeleteModal,
	setUploadProgress,
	resetFileState,
	resetDownloadState,
} = fileSlice.actions;

export default fileSlice.reducer;
